import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useI18n, formatCurrency, formatDate } from '@/lib/i18n';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

export default function Reports() {
  const { t, lang } = useI18n();
  const [diario, setDiario] = useState(null);
  const [mayor, setMayor] = useState(null);
  const [profit, setProfit] = useState(null);
  const load = useCallback(async () => {
    const [d, m, p] = await Promise.all([
      api.get('/reports/diario'),
      api.get('/reports/mayor'),
      api.get('/reports/profitability', { params: { days: 30 } }),
    ]);
    setDiario(d.data); setMayor(m.data); setProfit(p.data);
  }, []);
  useEffect(() => { load(); }, [load, load]);

  return (
    <div className="space-y-4" data-testid="reports-page">
      <h1 className="text-2xl font-heading font-bold">{t('nav_reports')}</h1>
      <Tabs defaultValue="diario">
        <TabsList>
          <TabsTrigger value="diario" data-testid="reports-tab-diario">{t('report_diario')}</TabsTrigger>
          <TabsTrigger value="mayor" data-testid="reports-tab-mayor">{t('report_mayor')}</TabsTrigger>
          <TabsTrigger value="profitability" data-testid="reports-tab-profitability">{t('report_profitability')}</TabsTrigger>
        </TabsList>
        <TabsContent value="diario">
          <Card><CardContent className="p-0">
            {!diario ? <Skeleton className="h-40 m-4" /> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[hsl(var(--muted))]"><tr>
                    <th className="text-left px-3 py-2 text-xs uppercase">{t('date')}</th>
                    <th className="text-left px-3 py-2 text-xs uppercase">{t('description')}</th>
                    <th className="text-left px-3 py-2 text-xs uppercase">PGC</th>
                    <th className="text-right px-3 py-2 text-xs uppercase">{t('debit')}</th>
                    <th className="text-right px-3 py-2 text-xs uppercase">{t('credit')}</th>
                  </tr></thead>
                  <tbody>{diario.entries.map((entry, i) => (
                    <tr key={`diario-${entry.fecha}-${entry.pgc_codigo}-${i}`} className="border-t">
                      <td className="px-3 py-1.5 text-xs">{formatDate(entry.fecha, lang)}</td>
                      <td className="px-3 py-1.5">{entry.descripcion}</td>
                      <td className="px-3 py-1.5"><Badge variant="outline" className="font-mono text-xs">{entry.pgc_codigo}</Badge></td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-mono text-[hsl(0_70%_40%)]">{entry.debe ? formatCurrency(entry.debe, lang) : ''}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-mono text-[hsl(142_60%_30%)]">{entry.haber ? formatCurrency(entry.haber, lang) : ''}</td>
                    </tr>
                  ))}</tbody>
                  <tfoot className="bg-[hsl(var(--muted))]"><tr>
                    <td colSpan={3} className="px-3 py-2 font-semibold">{t('total')}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-mono font-bold">{formatCurrency(diario.total_debe, lang)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-mono font-bold">{formatCurrency(diario.total_haber, lang)}</td>
                  </tr></tfoot>
                </table>
              </div>
            )}
          </CardContent></Card>
        </TabsContent>
        <TabsContent value="mayor">
          <Card><CardContent className="p-0">
            {!mayor ? <Skeleton className="h-40 m-4" /> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[hsl(var(--muted))]"><tr>
                    <th className="text-left px-3 py-2 text-xs uppercase">{t('pgc_code')}</th>
                    <th className="text-left px-3 py-2 text-xs uppercase">{t('pgc_name')}</th>
                    <th className="text-left px-3 py-2 text-xs uppercase">{t('pgc_group')}</th>
                    <th className="text-left px-3 py-2 text-xs uppercase">{t('pgc_type')}</th>
                    <th className="text-right px-3 py-2 text-xs uppercase">{t('total')}</th>
                  </tr></thead>
                  <tbody>{mayor.rows.map((r) => (
                    <tr key={`mayor-${r.tipo}-${r.codigo}`} className="border-t">
                      <td className="px-3 py-1.5 font-mono text-xs">{r.codigo}</td>
                      <td className="px-3 py-1.5">{r.nombre}</td>
                      <td className="px-3 py-1.5 text-xs">{r.grupo}</td>
                      <td className="px-3 py-1.5"><Badge variant={r.tipo === 'gasto' ? 'destructive' : 'default'} className="text-xs">{r.tipo}</Badge></td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-mono">{formatCurrency(r.total, lang)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
          </CardContent></Card>
        </TabsContent>
        <TabsContent value="profitability">
          <Card><CardContent className="p-0">
            {!profit ? <Skeleton className="h-40 m-4" /> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[hsl(var(--muted))]"><tr>
                    <th className="text-left px-3 py-2 text-xs uppercase">{t('name')}</th>
                    <th className="text-right px-3 py-2 text-xs uppercase">{t('quantity')}</th>
                    <th className="text-right px-3 py-2 text-xs uppercase">{t('base')}</th>
                    <th className="text-right px-3 py-2 text-xs uppercase">{t('total')}</th>
                  </tr></thead>
                  <tbody>{profit.rows.map((r) => (
                    <tr key={`prof-${r._id}`} className="border-t">
                      <td className="px-3 py-1.5">{r._id}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-mono">{r.cantidad}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-mono">{formatCurrency(r.base, lang)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-mono font-semibold">{formatCurrency(r.total, lang)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
